/* global Word, console */
import { CoObjectItem, DocumentPartSource } from "../components/interfaces/CoObject";
import { ElaborationStatusItem } from "../redux/feature/object/remote-elaborations-slice";
import NotificationManager from "../common/notification/NotificationManager";
import { DELETE_OBJECT_NOTIFICATION } from "../utils/constants/notification";
import { formatMessage, formatMessageWithParams } from "../utils/helpers/LanguageHelper";
import { getPublicResourceAsBase64Binary } from "../utils/axios";
import {
  addContentControlCustomXml, buildObjectFromXMLString,
  CO_OBJECT_NAMESPACE,
  deleteXMLPart,
  getCustomXmlByNamespaceId
} from "../utils/helpers/CustomXmlHelper";
import { mapDocumentPartConfigToDocumentPartItem } from "../redux/feature/cpm/map-objects";
import { CoObjectDocumentPartInclude, CoObjectTypeEnum } from "../api/models";
import RequestContext = Word.RequestContext;
import ContentControl = Word.ContentControl;

export async function updateContentControlWithElabResult(item: ElaborationStatusItem): Promise<void> {
  return Word.run(async (context) => {
    const cc = context.document.contentControls.getById(Number(item.contentControlId));
    await disableCannotDelete(cc);
      if (item.elabResultType === "HTML") {
        cc.clear();
        await context.sync();
        cc.insertHtml(item.elabResult, Word.InsertLocation.start);
      } else if (item.elabResultType === "STRINGBASE64") {
        cc.clear();
        await context.sync();
        cc.insertFileFromBase64(item.elabResult, Word.InsertLocation.start);
        console.time("handleParagraph")
        const firstParagraph = cc.paragraphs.getFirstOrNullObject();
        await context.sync();

        if (!firstParagraph.isNullObject) {
          // Update the font size of the first paragraph
          firstParagraph.font.size = 1;
          console.log(`Font size of the first paragraph in content control set to 1.`);
        } else {
          console.log("No paragraphs found in the specified content control.");
        }
        console.timeEnd("handleParagraph")
      } else if (item.elabResultType === "IMAGE_STRINGBASE64") {
        const keepSizeMessage = item.messages?.find((message: any) => message.severity === null);
        const imageData: ImageElabMessageData | undefined = keepSizeMessage
          ? JSON.parse(keepSizeMessage.details)
          : undefined;
        if (imageData && imageData.keepSize) {
          cc.load("inlinePictures");
          await context.sync();
          const oldImage = cc.inlinePictures.getFirstOrNullObject();
          await context.sync();
          if (!oldImage.isNullObject) {
            oldImage.load("height, width");
            await context.sync();
            const { height, width } = oldImage;
            const newImage = cc.insertInlinePictureFromBase64(item.elabResult, Word.InsertLocation.replace);
            newImage.set({ height, width });
          } else cc.insertInlinePictureFromBase64(item.elabResult, Word.InsertLocation.replace);
        } else cc.insertInlinePictureFromBase64(item.elabResult, Word.InsertLocation.replace);
      } else if (item.elabResultType === "REF_URL") {
        const refUrl = item.elabResult;
        const refUrlTargetContent = await getPublicResourceAsBase64Binary(refUrl);
        await modifyCannotEdit(context, cc, false);
        cc.clear();
        cc.insertFileFromBase64(refUrlTargetContent, Word.InsertLocation.start);
        await context.sync();
        await modifyCannotEdit(context, cc, true);
      } else {
        // eslint-disable-next-line office-addins/no-empty-load
        cc.load();
        await context.sync();
        cc.insertText(item.elabResult, Word.InsertLocation.replace);
        cc.cannotEdit = false;
      }
      cc.cannotDelete = true;

    await context.sync();
  });
}

// TODO
// this function must be used only for Word Web.
// it works only with insert, NOT works for the edit.
// it is related to DEV-114620 and it will be removed when MS will solve TrackingID#2402010030005817
export async function updateWebContentControlWithElabResult(item: ElaborationStatusItem): Promise<void> {
  return Word.run(async (context) => {
    const cc = context.document.contentControls.getById(Number(item.contentControlId));
    await disableCannotDelete(cc);
     if (item.elabResultType === "HTML") {
        cc.insertHtml(item.elabResult, Word.InsertLocation.start);
      } else if (item.elabResultType === "STRINGBASE64") {
        cc.insertFileFromBase64(item.elabResult, Word.InsertLocation.start);
        // Set the font size of the first paragraph
        const paragraphs = cc.paragraphs;
        paragraphs.load("items");

        await context.sync();

        if (paragraphs.items.length > 0) {
          // Update the font size of the first paragraph
          const firstParagraph = paragraphs.items[0];
          firstParagraph.font.size = 1;

          await context.sync();
          console.log(`Font size of the first paragraph in content control set to 1.`);
        } else {
          console.log("No paragraphs found in the specified content control.");
        }
      } else if (item.elabResultType === "IMAGE_STRINGBASE64") {
        const imageData: ImageElabMessageData = JSON.parse(item.messages[0].details);
        if (imageData.keepSize) {
          cc.load("inlinePictures");
          await context.sync();
          const oldImage = cc.inlinePictures.getFirstOrNullObject();
          await context.sync();
          if (!oldImage.isNullObject) {
            oldImage.load("height, width");
            await context.sync();
            const { height, width } = oldImage;
            const newImage = cc.insertInlinePictureFromBase64(item.elabResult, Word.InsertLocation.replace);
            newImage.set({ height, width });
          } else cc.insertInlinePictureFromBase64(item.elabResult, Word.InsertLocation.replace);
        } else {
          cc.insertInlinePictureFromBase64(item.elabResult, Word.InsertLocation.start);
        }
      } else if (item.elabResultType === "REF_URL") {
        const refUrl = item.elabResult;
        const refUrlTargetContent = await getPublicResourceAsBase64Binary(refUrl);
        cc.insertFileFromBase64(refUrlTargetContent, Word.InsertLocation.replace);
      } else {
        // eslint-disable-next-line office-addins/no-empty-load
        cc.load();
        await context.sync();
        cc.insertText(item.elabResult, Word.InsertLocation.replace);
        cc.cannotEdit = false;
      }
      cc.cannotDelete = true;
    await context.sync();
  });
}

export function createContentControlForCoObject(coObject: CoObjectItem): Promise<number> {
  return Word.run(async (context) => {
    const range = context.document.getSelection();
    range.clear();
    const wordContentControl = range.insertContentControl();
    wordContentControl.cannotDelete = true;
    wordContentControl.tag = coObject.info.objectName;
    wordContentControl.title = coObject.info.objectName;
    wordContentControl.cannotEdit = false;
    wordContentControl.insertParagraph("", Word.InsertLocation.after)
    wordContentControl.insertParagraph("", Word.InsertLocation.before)
    await context.sync();
    return wordContentControl.id;
  });
}

export function enableMoveCC(id: string, cannotDelete: boolean): Promise<any> {
  return Word.run(async (context) => {
    const wordContentControl: Word.ContentControl = context.document.contentControls.getById(Number(id));
    wordContentControl.cannotDelete = cannotDelete;
    await context.sync();
  });
}

export function lockObjectCannotEdit(id: string, cannotEdit: boolean): Promise<any> {
  return Word.run(async (context) => {
    const wordContentControl: Word.ContentControl = context.document.contentControls.getById(Number(id));
    wordContentControl.cannotEdit = cannotEdit;
    await context.sync();
  });
}

export function deleteContentControlById(id: string): Promise<any> {
  return Word.run(async (context) => {
    const wordContentControl: Word.ContentControl = context.document.contentControls.getById(Number(id));
    await disableCannotDelete(wordContentControl);
    wordContentControl.cannotDelete = false;
    wordContentControl.delete(false);
    await context.sync();
  }).catch((error) => {
    console.error(error);
    NotificationManager.addNotification({
      key: DELETE_OBJECT_NOTIFICATION,
      message: formatMessageWithParams("cof.elaboration.deleteobject.message.notdelete", {
        coObjectName: "coObject",
        exception: error.message,
      }),
      title: formatMessage("cof.createnewobject.message.deletefailed.title"),
      type: "error",
      closable: true,
      removeOnClose: true,
      isTemporary: false,
    });
    return Promise.reject(error);
  });
}

export async function disableCannotDelete(wordContentControl: Word.ContentControl): Promise<void> {
  try {
    if (wordContentControl) {
      wordContentControl.contentControls.load("items");
      await wordContentControl.contentControls.context.sync();
      wordContentControl.contentControls.items.forEach((cc) => {
        cc.cannotDelete = false;
        disableCannotDelete(cc);
      });
    }
  } catch (error) {
    // eslint-disable-next-line no-empty
    console.info("Error on disableCannotDelete", error);
  }
}

export async function modifyCannotEdit(
  context: RequestContext,
  contentControl: Word.ContentControl,
  edit: boolean
): Promise<void> {
  try {
    if (contentControl) {
      contentControl.contentControls.load("items");
      await context.sync();
      contentControl.cannotEdit = edit;
      try {
        let items = contentControl.contentControls.items;
        for (let child of items) {
          await modifyCannotEdit(context, child, edit);
        }
        // eslint-disable-next-line no-empty
      } catch (error) {
        console.info("Error modifing cannot edit: ", error);
      }
    }
  } catch (error) {
    console.info("Error modifing cannot edit: ", error);
  }
}

export function updateInfo(coObject: CoObjectItem, tag?: string) {
  return Word.run(async (context) => {
    const wordContentControl = context.document.contentControls.getById(Number(coObject.contentControlId));
    wordContentControl.title = coObject.info.objectName;
    if (tag) wordContentControl.tag = tag;
    await context.sync();
  }).catch((error) => {
    // eslint-disable-next-line no-undef
    console.log(error);
  });
}

export function updateContentControlName(contentControlId: number, newName: string) {
  return Word.run(async (context) => {
    const wordContentControl = context.document.contentControls.getById(contentControlId);
    wordContentControl.title = newName;
    await context.sync();
  }).catch((error) => {
    // eslint-disable-next-line no-undef
    console.error(error);
  });
}

export function selectContentControl(coObject: CoObjectItem) {
  return Word.run(async (context) => {
    let contentControl = context.document.contentControls.getById(Number(coObject.contentControlId));
    contentControl.select();
    await context.sync();
    return true;
  }).catch((error) => {
    // eslint-disable-next-line no-undef
    console.error(error);
    return false;
  });
}

export function getSubPartContentControl(contentControlId: string) {
  return Word.run(async (context) => {
    const contentControl = context.document.contentControls.getById(Number(contentControlId));
    let lastParent: Word.ContentControl;
    try {
      // eslint-disable-next-line office-addins/no-navigational-load
      contentControl.load("parentContentControl");
      await context.sync();
      lastParent = contentControl.parentContentControl;
    } catch (e) {
      // the content control is on the root document and
      return false;
    }

    let parent = lastParent;
    while (parent != null) {
      try {
        lastParent.load("id,parentContentControl");
        // eslint-disable-next-line office-addins/no-context-sync-in-loop
        await context.sync();
        parent = lastParent.parentContentControl;
        lastParent = parent;
      } catch (e) {
        parent = null;
      }
    }

    return await getCustomXmlByNamespaceId(lastParent.id.toString());
  });
}

/**
 * return true if the content control last parent in the content control hierarchy
 * is a coObject of type DOCUMENT_PART_INCLUDE by default or a different type
 * @param contentControlId
 * @param objectType
 */
export async function isSubPartContentControl(
  contentControlId: string,
  objectType: CoObjectTypeEnum = CoObjectTypeEnum.DOCUMENT_PART_INCLUDE
): Promise<boolean> {
  const lastParentCoObject = await getSubPartContentControl(contentControlId);
  return lastParentCoObject != false && lastParentCoObject.info.objectType == objectType;
}

function parseElabResult<T extends { coObjectDocumentPart: unknown }>(jsonDetails: string): T {
  const data = JSON.parse(jsonDetails) as T & { coObjectDocumentPart: string };
  data.coObjectDocumentPart = JSON.parse(data.coObjectDocumentPart);
  return data
}

function createCoObjectDataObj(docPartCoObject: CoObjectDocumentPartInclude , sharepointPath: string):Omit<CoObjectItem, "source"> & { source: DocumentPartSource } {
  return {
    id: docPartCoObject.id,
    documentId: docPartCoObject.documentId,
    contentControlId: docPartCoObject.contentControlId,
    info: {
      objectName: docPartCoObject.name,
      info: docPartCoObject.desc,
      objectType: CoObjectTypeEnum.DOCUMENT_PART_INCLUDE,
    },
    source: {
      path: sharepointPath,
      includedDocumentPartId: docPartCoObject.includedDocumentPartId,
      type: "DOCUMENT_PART_INCLUDE"
    }
  };
}

export const handleDetachElabResult = async (item: ElaborationStatusItem) => {
  const { coObjectDocumentPart, oldContentControlId } = parseElabResult<DetachElabMessageData>(item.messages[0].details)
  // server formatting options handling for nested objects
  const coObject = createCoObjectDataObj(coObjectDocumentPart, (item as unknown as { sharepointPath: string }).sharepointPath)

  await addContentControlCustomXml(coObject.contentControlId, mapDocumentPartConfigToDocumentPartItem(coObject));
  await updateInfo(coObject);

  await deleteXMLPart(oldContentControlId);
  await deleteContentControlById(oldContentControlId);
};

export const handleCloneElabResult = async (item: ElaborationStatusItem) => {
  const { coObjectDocumentPart } = parseElabResult<CloneElabMessageData>(item.messages[0].details)
  // server formatting options handling for nested objects
  const coObject = createCoObjectDataObj(coObjectDocumentPart, (item as unknown as { sharepointPath: string }).sharepointPath)

  await addContentControlCustomXml(coObject.contentControlId, mapDocumentPartConfigToDocumentPartItem(coObject));
  await updateInfo(coObject);
};

export const cleanDocumentFromCC = async (contentControlId: string) => {
  await deleteContentControlById(contentControlId);
  try {
    await deleteXMLPart(contentControlId);
  } catch (e) {
    // eslint-disable-next-line no-empty
    console.error("Error on cleanDocumentFromCC ", e);
  }
};

export interface DetachElabMessageData {
  oldContentControlId: string;
  coObjectDocumentPart: CoObjectDocumentPartInclude;
}

export interface CloneElabMessageData {
  coObjectDocumentPart: CoObjectDocumentPartInclude;
}

export interface ImageElabMessageData {
  keepSize: boolean;
}

export async function getParentContentControls(): Promise<Word.ContentControl | null> {
  return Word.run(async (context) => {
    try {
      const range = context.document.getSelection();
      context.load(range);
      await context.sync();
      const parentContentControl = range.parentContentControlOrNullObject;
      context.load(parentContentControl, "id");
      await context.sync();
      if (parentContentControl.isNullObject) {
        console.warn("No parent content control in selection ", { range });
        return null;
      }
      const contentControl = context.document.contentControls.getById(parentContentControl.id);
      contentControl.load(["cannotEdit", "cannotDelete"]);
      await context.sync();
      return contentControl;
    } catch (err) {
      console.error("ERROR GETTING PARENT CC", err);
      return null;
    }
  });
}

export async function getContentControls(): Promise<Word.ContentControl[] | null> {
  return Word.run(async (context) => {
    try {
      const range = context.document.getSelection();
      range.load(["items"]);
      await context.sync();
      const cc = range.contentControls;
      cc.load(["items", "cannotEdit", "cannotDelete"]);
      await context.sync();
      const ccs = cc.items;
      for (let c of ccs) {
        c.load(["cannotEdit", "cannotDelete"]);
        await context.sync();
      }
      return ccs;
    } catch (_) {
      return null;
    }
  });
}

export function getContentControlsWithFirstLevelChild(): Promise<Word.ContentControl[] | null> {
  return Word.run(async context => {
    try {
      context.document.contentControls.load(["items"]);
      await context.sync();
      const ccs = context.document.contentControls.items;
      const firstLevelCCs = [];
      for(const cc of ccs) {
        cc.load(["id", "title"]);
        const parentContentControl = cc.parentContentControlOrNullObject
        await context.sync();
        if(parentContentControl.isNullObject) {
          firstLevelCCs.push(cc);
          continue;
        }

        const xmlPointer = context.document.customXmlParts.getByNamespace(CO_OBJECT_NAMESPACE + parentContentControl.id)
        xmlPointer.load(["items"])
        await context.sync();
        const xmlItem = xmlPointer.items[0]
        if(!xmlItem || xmlItem.isNullObject) continue;
        await context.sync();
        const xmlResult = xmlItem?.getXml()
        await context.sync();
        const xml = xmlResult?.value
        if(!xml) continue;

        const xmlObject = buildObjectFromXMLString(xml)
        if(xmlObject?.info?.objectType !== CoObjectTypeEnum.FREE_TEXT) continue;

        parentContentControl.load(["id", "title"]);
        const parentOfParent = parentContentControl.parentContentControlOrNullObject
        await context.sync();

        if(parentOfParent.isNullObject) {
          firstLevelCCs.push(cc);
        }
      }
      return firstLevelCCs;
    } catch (e) {
       console.error(e)
       return null;
    }
  });
}

export async function checkIfSelected(): Promise<ContentControl | ContentControl[]> {
    const [parent, ccs] = await Promise.all([getParentContentControls(), getContentControls()]);

    if (!(parent || ccs?.length)) {
        return Promise.resolve(null);
    }
    if (parent && ccs?.length) {
        //if both exist and parent is a freetext i'm selecting a cc inside a freetext
        const coObjectItem = await getCustomXmlByNamespaceId(parent.id.toString());
        if (coObjectItem && coObjectItem.info.objectType == CoObjectTypeEnum.FREE_TEXT) {
            return Promise.resolve(ccs)
        }
    }
    return Promise.resolve(parent || ccs);
}

export async function isCursorInsideTable() {
  try {
    return await Word.run(async (context) => {
      const selection = context.document.getSelection();
      if (!selection) {
        return false;
      }

      selection.load("parentTable");

      await context.sync();
      return Boolean(selection.parentTable);
    });
  } catch (error) {
    console.error("Errore in isCursorInsideTable:", error);
    return false;
  }
}




export async function isAddNewPossible() {
  try {
    const ccs = await checkIfSelected();
    const isCCArray = Array.isArray(ccs);
    if (isCCArray) {
      return ccs.every(async (c) => {
        const coObjectItem = await getCustomXmlByNamespaceId(c.id + "");
        return coObjectItem?.info?.objectType == "FREE_TEXT";
      });
    }
    const coObjectItem = await getCustomXmlByNamespaceId(ccs.id + "");
    return coObjectItem?.info?.objectType == "FREE_TEXT";
  } catch (e) {
    return true;
  }
}
